import { useEffect } from 'react'

const SEOHead = ({
  title = 'TheBoomer - AI Directory | Discover the Best AI Tools & Companies',
  description = 'Discover the most powerful AI tools, companies, and contributors across different categories. From language models to image generators, find the perfect AI solution for your needs.',
  keywords = 'AI tools, artificial intelligence, AI directory, AI companies, machine learning, AI contributors, AI timeline, ChatGPT, AI generators, AI models',
  ogImage = 'https://theboomer.ai/og-image.svg',
  url = '/',
  type = 'website',
}) => {
  useEffect(() => {
    const baseUrl = 'https://theboomer.ai'
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

    // Update document title
    document.title = title

    // Update or create meta tags
    const updateMetaTag = (name, content, property = false) => {
      const attribute = property ? 'property' : 'name'
      let meta = document.querySelector(`meta[${attribute}="${name}"]`)

      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute(attribute, name)
        document.head.appendChild(meta)
      }

      meta.setAttribute('content', content)
    }

    // Primary meta tags
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)

    // Open Graph tags
    updateMetaTag('og:type', type, true)
    updateMetaTag('og:url', fullUrl, true)
    updateMetaTag('og:title', title, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:image', ogImage, true)

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true)
    updateMetaTag('twitter:url', fullUrl, true)
    updateMetaTag('twitter:title', title, true)
    updateMetaTag('twitter:description', description, true)
    updateMetaTag('twitter:image', ogImage, true)

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', fullUrl)
  }, [title, description, keywords, ogImage, url, type])

  return null // This component doesn't render anything
}

// Page-specific SEO configurations
export const seoConfigs = {
  home: {
    title: 'TheBoomer - AI Directory | Discover the Best AI Tools & Companies',
    description:
      'Discover the most powerful AI tools, companies, and contributors across different categories. From language models to image generators, find the perfect AI solution for your needs.',
    keywords:
      'AI tools, artificial intelligence, AI directory, AI companies, machine learning, AI contributors, AI timeline, ChatGPT, AI generators, AI models',
    url: '/',
    ogImage: 'https://theboomer.ai/og-image.svg',
  },

  tools: {
    title: 'AI Tools Directory | Best AI Tools for Every Need - theBoomer.ai',
    description:
      'Browse our comprehensive collection of AI tools including ChatGPT, image generators, code assistants, and more. Find the perfect AI tool for your specific needs.',
    keywords:
      'AI tools, ChatGPT, AI image generators, AI code assistants, AI writing tools, machine learning tools, AI productivity tools',
    url: '/tools',
    ogImage: 'https://theboomer.ai/og-image.svg',
  },

  companies: {
    title:
      'AI Companies Directory | Leading AI Startups & Enterprises - theBoomer.ai',
    description:
      'Explore leading AI companies, startups, and enterprises shaping the future of artificial intelligence. Discover their expertise, funding, and innovations.',
    keywords:
      'AI companies, AI startups, artificial intelligence companies, AI enterprises, machine learning companies, AI funding, AI innovations',
    url: '/companies',
    ogImage: 'https://theboomer.ai/og-image.svg',
  },

  contributors: {
    title: 'AI Agents | Enterprise Ready Agents - theBoomer.ai',
    description: 'Checkout industry ready agents',
    keywords: 'AI agents, AI Enterprise, AI flows, SAAS agents, Agentic, RAG',
    url: '/contributors',
    ogImage: 'https://theboomer.ai/og-image.svg',
  },

  timeline: {
    title:
      'AI Evolution Timeline | History of Artificial Intelligence - theBoomer.ai',
    description:
      'Explore the complete timeline of AI evolution from 1940s to 2025+. Discover key milestones, breakthroughs, and innovations in artificial intelligence history.',
    keywords:
      'AI timeline, AI history, artificial intelligence evolution, AI milestones, AI breakthroughs, machine learning history, AI development',
    url: '/timeline',
    ogImage: 'https://theboomer.ai/og-image.svg',
  },
}

export default SEOHead
